import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import i18n from 'i18n';
import { cgToast, notifyError } from 'modules/Common/ui/toast';
import {
  fetchSupplierFactorHistory,
  fetchSupplierFactors,
  withdrawSupplierFactor,
} from './supplierFactorApi';
import { isSupplierFactorPropagationTerminal } from './supplierFactorContracts';
import { SUPPLIER_INBOX_QUERY_KEY } from './useSupplierInbox';

export const SUPPLIER_FACTORS_QUERY_KEY = ['supplierFactors'];
export const supplierFactorHistoryQueryKey = (anchorId) => [
  ...SUPPLIER_FACTORS_QUERY_KEY,
  'history',
  anchorId,
];

// Propagation is async and NOT pushed (no websocket/SSE — S10 drain worker
// deferred). list/publish responses carry a terminal snapshot today, but the
// badge must keep refetching so a queued/processing item (once the worker exists)
// advances to synced/degraded. Poll only while something is non-terminal.
const SUPPLIER_FACTORS_POLL_MS = 5000;

const anyPropagationNonTerminal = (data) =>
  (data?.items || []).some((item) => !isSupplierFactorPropagationTerminal(item.propagation?.state));

export const useSupplierFactors = ({ enabled = true } = {}) =>
  useQuery({
    queryKey: SUPPLIER_FACTORS_QUERY_KEY,
    queryFn: fetchSupplierFactors,
    staleTime: 0,
    enabled,
    refetchInterval: (query) =>
      anyPropagationNonTerminal(query.state.data) ? SUPPLIER_FACTORS_POLL_MS : false,
  });

export const useSupplierFactorHistory = (historyAnchorRevisionId, { enabled = true } = {}) =>
  useQuery({
    queryKey: supplierFactorHistoryQueryKey(historyAnchorRevisionId),
    queryFn: () => fetchSupplierFactorHistory(historyAnchorRevisionId),
    enabled: enabled && !!historyAnchorRevisionId,
    staleTime: 0,
  });

// The single mutation triggered outside the reused emissions modal. Publish and
// correct flow through the modal's injected http (mapped inline), then the page's
// handleRefresh invalidates SUPPLIER_FACTORS_QUERY_KEY. Withdraw is a row action,
// so it owns its own mutation + invalidation here.
export const useSupplierFactorActions = () => {
  const queryClient = useQueryClient();

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: SUPPLIER_FACTORS_QUERY_KEY });
    // A withdrawal/publication can change data-request fulfilment coverage.
    await queryClient.invalidateQueries({ queryKey: SUPPLIER_INBOX_QUERY_KEY });
  };

  const withdraw = useMutation({
    mutationFn: (revisionId) => withdrawSupplierFactor(revisionId),
    onSuccess: async () => {
      cgToast.success(i18n.t('freeSupplier:toasts.withdrawnTitle', 'Withdrawn'), {
        description: i18n.t(
          'freeSupplier:toasts.factorWithdrawnBody',
          'The factor has been withdrawn from your shares',
        ),
      });
      await invalidate();
    },
    // In-use revisions surface the backend's verbatim reason (D-10).
    onError: notifyError,
  });

  return { withdraw, invalidate };
};
